<script setup lang="ts">
  import { computed, nextTick, onMounted, onUnmounted, ref, watch, watchEffect } from "vue";
  import { useRoute, useRouter } from "vue-router";
  import { useRapportenStore } from "../stores/rapporten";
  import {
    getInspectieTypeParam,
    getPreferredInspectieType,
    getRapportDetailsPath,
    getRapportHerstelPath,
    isCertificaatBeschikbaar,
  } from "../utils/rapportScope";
  import { useHerstelSubmission } from "../composables/useHerstelSubmission";
  import { useInspectiecertificaat } from "../composables/useInspectiecertificaat";
  import {
    openGalleryAt,
    registerGalleryImage,
    unregisterGalleryImagesByPrefix,
    updateGalleryThumbEl,
  } from "../composables/usePhotoSwipeGallery";
  import { matchesFotoContextLabel, matchesFotoContextPrefix } from "../utils/fotoContextLabels";

  // Components
  import AppHeader from "../components/AppHeader.vue";
  import CustomTag from "../components/CustomTag.vue";
  import DocumentVereistenSection from "../components/DocumentVereistenSection.vue";
  import HerstelInstructies from "../components/HerstelInstructies.vue";
  import NotitiesSection from "../components/NotitiesSection.vue";
  import RapportPdfCard from "../components/RapportPdfCard.vue";
  import ShareRapportDialog from "../components/ShareRapportDialog.vue";

  // PrimeVue
  import Button from "primevue/button";
  import Popover from "primevue/popover";
  import ProgressSpinner from "primevue/progressspinner";

  // Stores
  import { useAuthStore } from "../stores/auth";

  // Types
  import {
    inspectieTypeLabels,
    rapportStatusLabels,
    type InspectieStatus,
    type InspectieType,
    type RapportStatus,
  } from "../../../../types";

  interface DetailFoto {
    id: string;
    remote_url: string | null;
    context_label: string;
    isFallback?: boolean;
  }

  const route = useRoute();
  const router = useRouter();
  const rapportenStore = useRapportenStore();
  const authStore = useAuthStore();
  const thumbRefs = ref<Record<string, HTMLElement | null>>({});
  const routeRapportId = computed(() => String(route.params.id || ""));
  const activeInspectieType = computed(() => getInspectieTypeParam(route.params.type));
  const pswpGalleryId = computed(() => `rapport-detail-${routeRapportId.value || "unknown"}`);

  const statusLabels = rapportStatusLabels;

  // Sharing: only opdrachtgevers manage rapport access. The rapport row itself
  // is only visible to opdrachtgevers with access, so role + loaded rapport is
  // enough — no extra junction query needed.
  const sharePopover = ref();
  const shareDialogVisible = ref(false);
  const shareDialogType = ref<"opdrachtgever" | "installateur">("opdrachtgever");
  const canShare = computed(() => !!authStore.profileMeta?.isOpdrachtgever);

  onMounted(() => {
    authStore.ensureProfileMeta().catch((error) => {
      console.warn("Could not resolve profile meta for share button:", error);
    });
  });

  const toggleSharePopover = (event: Event) => {
    sharePopover.value?.toggle(event);
  };

  const openShareDialog = (type: "opdrachtgever" | "installateur") => {
    sharePopover.value?.hide();
    shareDialogType.value = type;
    shareDialogVisible.value = true;
  };

  const loadRapport = async () => {
    const id = routeRapportId.value;
    if (!id) {
      router.push("/rapporten");
      return;
    }

    const data = await rapportenStore.fetchRapportById(id);
    if (!data) {
      router.push("/rapporten");
    }
  };

  onMounted(() => {
    void loadRapport();
  });

  watch(
    routeRapportId,
    (nextId, previousId) => {
      if (!nextId || nextId === previousId) return;
      void loadRapport();
    },
  );

  onUnmounted(() => {
    rapportenStore.unsubscribeFromRapport();
  });

  const rapport = computed(() => rapportenStore.currentRapport);

  const activeInspectieEntry = computed<InspectieStatus | null>(() => {
    if (!activeInspectieType.value || !Array.isArray(rapport.value?.inspectieStatus)) return null;
    return rapport.value.inspectieStatus.find((entry) => entry.type === activeInspectieType.value) || null;
  });

  const activeInspectieLabel = computed(() => {
    if (!activeInspectieType.value) return "";
    return inspectieTypeLabels[activeInspectieType.value as InspectieType] || activeInspectieType.value;
  });

  const detailsTitle = computed(() => {
    return activeInspectieLabel.value ? `Rapport ${activeInspectieLabel.value}` : "Rapport Details";
  });

  // Herstelverklaring submission state, stored in public.herstel_submissions
  const { submission: herstelSubmission, loadSubmission: loadHerstelSubmission } = useHerstelSubmission();
  const {
    generating: certificaatGenerating,
    downloadInspectiecertificaat,
    downloadSciosCertificaat,
  } = useInspectiecertificaat();

  watch(
    [routeRapportId, activeInspectieType],
    ([rapportId, inspectieType]) => {
      if (!rapportId || !inspectieType) return;
      void loadHerstelSubmission(rapportId, inspectieType);
    },
    { immediate: true },
  );

  const isScopeSubmitted = computed(() => Boolean(herstelSubmission.value?.submitted_at));

  const scopeSubmissionDate = computed<string | null>(() => herstelSubmission.value?.submitted_at ?? null);

  const filteredConstateringen = computed<any[]>(() => {
    if (!activeInspectieType.value || !Array.isArray(rapport.value?.constateringen)) return [];

    return rapport.value.constateringen.filter((constatering: any) => {
      return Array.isArray(constatering.inspecties) && constatering.inspecties.includes(activeInspectieType.value);
    });
  });

  const hasRejectedConstateringen = computed(() => {
    return filteredConstateringen.value.some((constatering) => constatering.herstelStatus === "rejected");
  });

  const hasHerinspectie = computed(() => {
    return filteredConstateringen.value.some((constatering: any) => constatering.vereist_herinspectie);
  });

  const herinspectieStatusItems = computed(() => {
    return filteredConstateringen.value
      .filter((constatering: any) => constatering.vereist_herinspectie)
      .map((constatering: any) => {
      const communicatie = Array.isArray(constatering.herstelCommunicatie)
        ? constatering.herstelCommunicatie
        : [];
      const lastMessage = communicatie.length ? communicatie[communicatie.length - 1] : null;

      return {
        id: constatering.id,
        locatie: constatering.locatie || "Geen locatie",
        beoordeling: constatering.beoordeling,
        hersteld: Boolean(constatering.hersteld),
        bevestigd: constatering.herstelStatus === "accepted",
        herstelStatus: constatering.herstelStatus || null,
        opmerkingen: constatering.opmerkingen || "",
        lastMessage: lastMessage?.message || null,
      };
      });
  });

  const isScopeCompleted = computed(() => {
    return Boolean(activeInspectieEntry.value?.afgemeld || activeInspectieEntry.value?.certificaat);
  });

  const ensureValidActiveScope = () => {
    if (!rapport.value) return;

    const hasMatchingType =
      activeInspectieType.value &&
      Array.isArray(rapport.value.inspectieStatus) &&
      rapport.value.inspectieStatus.some((entry) => entry.type === activeInspectieType.value);

    if (hasMatchingType) return;

    const fallbackType = getPreferredInspectieType(rapport.value.inspectieStatus);
    if (!fallbackType) {
      router.replace("/rapporten");
      return;
    }

    const fallbackPath = getRapportDetailsPath(rapport.value.id, fallbackType);
    if (route.fullPath !== fallbackPath) {
      router.replace(fallbackPath);
    }
  };

  watch([rapport, activeInspectieType], ensureValidActiveScope, { immediate: true });

  const detailFotos = computed<DetailFoto[]>(() => {
    const fotos = (rapport.value as any)?.detailFotos;
    return Array.isArray(fotos) ? fotos : [];
  });

  const locatieFotoBoven = computed(() => {
    return detailFotos.value.find((foto) => matchesFotoContextLabel(foto.context_label, "foto_boven")) || null;
  });

  const locatieOverzichtFotos = computed(() => {
    return detailFotos.value.filter((foto) => matchesFotoContextPrefix(foto.context_label, "foto_overzicht"));
  });

  const pvOverzichtFotos = computed(() => {
    return detailFotos.value.filter((foto) => matchesFotoContextPrefix(foto.context_label, "foto_pv_overzicht"));
  });

  const installatieFotos = computed(() => {
    return detailFotos.value.filter(
      (foto) =>
        matchesFotoContextPrefix(foto.context_label, "installatie_foto") ||
        matchesFotoContextLabel(foto.context_label, "installatie_foto_pv"),
    );
  });

  const setThumbRef = (id: string, el: HTMLElement | null) => {
    if (el) {
      thumbRefs.value[id] = el;
    }
  };

  const constateringenSummary = computed(() => {
    const summary = { total: 0, opmerking: 0, gering: 0, serieus: 0, ernstig: 0, hersteld: 0 };
    if (filteredConstateringen.value.length === 0) return summary;

    for (const c of filteredConstateringen.value) {
      summary.total++;
      if (c.beoordeling === "opmerking") summary.opmerking++;
      else if (c.beoordeling === "gering") summary.gering++;
      else if (c.beoordeling === "serieus") summary.serieus++;
      else if (c.beoordeling === "ernstig") summary.ernstig++;
      if (c.hersteld) summary.hersteld++;
    }
    return summary;
  });

  const display = (v: any): string => {
    if (v === null || v === undefined || v === "") return "Niet beschikbaar";
    return String(v);
  };

  const getLocatieFotoLabel = (photo: DetailFoto, index: number) => {
    if (matchesFotoContextLabel(photo.context_label, "foto_boven")) {
      return photo.isFallback ? "Locatie foto boven" : "Rapport foto boven";
    }

    return `Foto overzicht ${index + 1}`;
  };

  const getInstallatieFotoLabel = (photo: DetailFoto, index: number) => {
    if (matchesFotoContextLabel(photo.context_label, "installatie_foto_pv")) {
      return "PV foto";
    }

    return `Installatie foto ${index + 1}`;
  };

  const getPvOverzichtFotoLabel = (_photo: DetailFoto, index: number) => {
    return `PV overzicht ${index + 1}`;
  };

  const galleryPhotos = computed(() => {
    const photos: Array<DetailFoto & { galleryLabel: string; galleryImageId: string }> = [];

    if (locatieFotoBoven.value?.remote_url) {
      photos.push({
        ...locatieFotoBoven.value,
        galleryLabel: getLocatieFotoLabel(locatieFotoBoven.value, 0),
        galleryImageId: `detail-${locatieFotoBoven.value.id}`,
      });
    }

    for (const [index, photo] of locatieOverzichtFotos.value.entries()) {
      if (!photo.remote_url) continue;
      photos.push({
        ...photo,
        galleryLabel: getLocatieFotoLabel(photo, index),
        galleryImageId: `detail-${photo.id}`,
      });
    }

    for (const [index, photo] of pvOverzichtFotos.value.entries()) {
      if (!photo.remote_url) continue;
      photos.push({
        ...photo,
        galleryLabel: getPvOverzichtFotoLabel(photo, index),
        galleryImageId: `detail-${photo.id}`,
      });
    }

    for (const [index, photo] of installatieFotos.value.entries()) {
      if (!photo.remote_url) continue;
      photos.push({
        ...photo,
        galleryLabel: getInstallatieFotoLabel(photo, index),
        galleryImageId: `detail-${photo.id}`,
      });
    }

    return photos;
  });

  const openPhotoPreview = (photo: DetailFoto) => {
    if (!photo.remote_url) return;
    openGalleryAt(pswpGalleryId.value, `detail-${photo.id}`);
  };

  watchEffect(() => {
    unregisterGalleryImagesByPrefix(pswpGalleryId.value, "detail-");
    for (const photo of galleryPhotos.value) {
      registerGalleryImage({
        galleryId: pswpGalleryId.value,
        imageId: photo.galleryImageId,
        src: photo.remote_url || "",
        label: photo.galleryLabel,
        thumbEl: thumbRefs.value[photo.id] || undefined,
        thumbSrc: photo.remote_url || undefined,
      });
    }
  });

  watch(
    thumbRefs,
    () => {
      nextTick(() => {
        for (const photo of galleryPhotos.value) {
          const el = thumbRefs.value[photo.id];
          if (el) {
            updateGalleryThumbEl(pswpGalleryId.value, photo.galleryImageId, el);
          }
        }
      });
    },
    { deep: true },
  );

  onUnmounted(() => {
    unregisterGalleryImagesByPrefix(pswpGalleryId.value, "detail-");
  });

  type ProcessStepState = "done" | "active" | "pending";

  interface ProcessStep {
    key: string;
    label: string;
    state: ProcessStepState;
  }

  const shouldShowProcessFooter = computed(() => {
    if (!activeInspectieEntry.value) return false;

    return (
      activeInspectieEntry.value.status === "voldoet_niet" ||
      activeInspectieEntry.value.status === "verlopen" ||
      activeInspectieEntry.value.status === "hersteld" ||
      isScopeSubmitted.value ||
      hasRejectedConstateringen.value ||
      isScopeCompleted.value
    );
  });

  const processSteps = computed<ProcessStep[]>(() => {
    const submitted = isScopeSubmitted.value;
    const completed = isScopeCompleted.value;

    return [
      { key: "inspectie", label: "Inspectie uitgevoerd", state: "done" },
      {
        key: "herstel",
        label: "Herstelverklaring aanleveren",
        state: completed || submitted ? "done" : "active",
      },
      {
        key: "controle",
        label: "Controle inspecteur",
        state: completed ? "done" : submitted ? "active" : "pending",
      },
      {
        key: "certificaat",
        label: "Certificaat",
        state: completed ? "active" : "pending",
      },
    ];
  });

  const processConnectorStates = computed(() => {
    return processSteps.value.slice(1).map((step) => step.state !== "pending");
  });

  const processCta = computed(() => {
    if (!shouldShowProcessFooter.value || !activeInspectieType.value || !rapport.value) return null;

    if (isScopeCompleted.value) {
      return {
        description: activeInspectieEntry.value?.certificaat
          ? `De inspectie ${activeInspectieLabel.value} is afgerond en het certificaat is beschikbaar.`
          : `De inspectie ${activeInspectieLabel.value} is afgerond en wacht op administratieve afronding.`,
        label: null,
        path: null,
      };
    }

    if (isScopeSubmitted.value) {
      return {
        description: hasHerinspectie.value
          ? `De herstelverklaring voor ${activeInspectieLabel.value} is ingediend. De inspecteur controleert nu ook de constateringen die voor herinspectie zijn gemarkeerd.`
          : `De herstelverklaring voor ${activeInspectieLabel.value} is ingediend. Wacht op controle door de inspecteur.`,
        label: null,
        path: null,
      };
    }

    return {
      description: hasRejectedConstateringen.value
        ? hasHerinspectie.value
          ? "Er zijn afgekeurde constateringen. Werk de herstelpunten en herstelfoto's bij en dien daarna opnieuw de herstelverklaring in. Voor gemarkeerde constateringen volgt daarna nog een fysieke herinspectie."
          : "Er zijn afgekeurde constateringen. Werk de herstelpunten bij en dien daarna opnieuw de herstelverklaring in."
        : hasHerinspectie.value
          ? "Lever de herstelpunten en herstelfoto's aan. Voor constateringen die als herinspectie zijn gemarkeerd volgt daarna nog een fysieke controle door de inspecteur."
          : "Er zijn constateringen die herstel vereisen. Dien een herstelverklaring in om het proces voort te zetten.",
      label: "Herstelpunten invullen",
      path: getRapportHerstelPath(rapport.value.id, activeInspectieType.value),
    };
  });

  // SCIOS scopes have an uploaded certificate PDF (certificaat_pad in userFiles);
  // NEN 1010 / NEN 3140 have no external SCIOS certificate, so once the scope is
  // completed (voldoet) the opdrachtgever can download the generated
  // inspectiecertificaat directly.
  const canDownloadCertificaat = computed(() => isCertificaatBeschikbaar(activeInspectieEntry.value));

  const handleDownloadCertificaat = async () => {
    if (!rapport.value || !activeInspectieType.value) return;
    const nr = rapport.value.inspectieRapportNr || rapport.value.id;
    const fileName = `Certificaat ${activeInspectieLabel.value} - ${nr}.pdf`;
    try {
      const certificaatPad = activeInspectieEntry.value?.certificaat_pad;
      if (activeInspectieEntry.value?.certificaat && certificaatPad) {
        await downloadSciosCertificaat(certificaatPad, fileName);
      } else {
        await downloadInspectiecertificaat(rapport.value.id, activeInspectieType.value as InspectieType, fileName);
      }
    } catch {
      // Errors are surfaced via toast inside the composable.
    }
  };

  // A constatering is "herstelbaar" (repair-worthy) when its beoordeling is a real
  // defect — matches the server's failing-finding definition in fn_compute_inspectie_status
  // (gering/serieus/ernstig; 'opmerking' is only a remark and never fails a scope).
  const hasHerstelbareConstateringen = computed(() => {
    return filteredConstateringen.value.some(
      (constatering: any) =>
        constatering.beoordeling === "gering" ||
        constatering.beoordeling === "serieus" ||
        constatering.beoordeling === "ernstig",
    );
  });

  // The process footer already renders a "Herstelpunten invullen" button in its CTA
  // while the scope is still open. Only render the standalone button when the footer
  // isn't already offering that action (e.g. once the scope is afgemeld/afgerond), so
  // an opdrachtgever can always reach the herstelpunten for a report with defects.
  const footerOffersHerstel = computed(() => Boolean(processCta.value?.label && processCta.value?.path));

  const showStandaloneHerstelButton = computed(
    () => hasHerstelbareConstateringen.value && !footerOffersHerstel.value,
  );

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "";
    try {
      return new Intl.DateTimeFormat("nl-NL", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      }).format(new Date(dateString));
    } catch {
      return "";
    }
  };
</script>

<template>
  <div class="rapport-details-page">
    <AppHeader
      :title="detailsTitle"
      :show-back-button="true"
      back-to="/rapporten"
      back-text="Terug naar rapporten"
    />

    <!-- Loading -->
    <div
      v-if="rapportenStore.loading"
      class="status-card status-loading"
    >
      <ProgressSpinner style="width: 50px; height: 50px" />
    </div>

    <!-- Error -->
    <div
      v-else-if="rapportenStore.error"
      class="status-card status-error"
    >
      {{ rapportenStore.error }}
    </div>

    <!-- Not found -->
    <div
      v-else-if="!rapport"
      class="status-card"
    >
      Rapport niet gevonden.
    </div>

    <div
      v-else-if="!activeInspectieEntry"
      class="status-card"
    >
      Inspectie type niet gevonden.
    </div>

    <!-- Content -->
    <template v-else>
      <!-- Header Card -->
      <div class="rapport-card header-card">
        <div class="header-card-top">
          <div>
            <h2 class="rapport-nr">{{ rapport.inspectieRapportNr || "Geen nummer" }}</h2>
            <p class="rapport-date">Aangemaakt op {{ formatDate(rapport.created_at) }}</p>
            <Button
              v-if="canShare"
              label="Delen met…"
              severity="secondary"
              outlined
              size="small"
              icon="pi pi-share-alt"
              class="mt-2"
              @click="toggleSharePopover"
            />
          </div>
          <!-- Inspectie Status -->
          <div>
            <div class="inspectie-cards">
              <div class="inspectie-card">
                <div class="inspectie-card-header">
                  <span class="inspectie-type">{{ activeInspectieLabel }}</span>
                  <CustomTag
                    :color="activeInspectieEntry.status"
                    :label="statusLabels[activeInspectieEntry.status as RapportStatus] || activeInspectieEntry.status"
                  />
                </div>
                <p
                  v-if="activeInspectieEntry.eindDatum"
                  class="inspectie-date"
                >
                  Uitgevoerd op: {{ formatDate(String(activeInspectieEntry.eindDatum)) }}
                </p>
                <div
                  v-if="canDownloadCertificaat"
                  class="inspectie-certificaat"
                >
                  <CustomTag
                    color="voldoet"
                    icon="pi pi-verified"
                    label="Certificaat beschikbaar"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <RapportPdfCard
        v-if="activeInspectieType"
        :rapport-id="rapport.id"
        :inspectie-type="activeInspectieType"
        :rapport-nr="rapport.inspectieRapportNr ?? null"
      >
        <template
          v-if="canDownloadCertificaat"
          #extra
        >
          <div class="pdf-document-row">
            <i
              class="pi pi-verified pdf-document-icon"
              aria-hidden="true"
            />
            <span class="pdf-document-name">Certificaat {{ activeInspectieLabel }}</span>
            <Button
              label="Download"
              severity="secondary"
              outlined
              icon="pi pi-download"
              :loading="certificaatGenerating"
              @click="handleDownloadCertificaat"
            />
          </div>
        </template>
      </RapportPdfCard>

      <Popover
        ref="sharePopover"
        class="z-1000"
      >
        <div class="flex w-56 flex-col gap-1">
          <button
            type="button"
            class="hover:bg-background-100 flex items-center gap-3 rounded-lg p-2 text-left transition-colors"
            @click="openShareDialog('opdrachtgever')"
          >
            <i class="pi pi-briefcase text-background-500" />
            <span>
              <span class="block font-medium">Opdrachtgever</span>
              <span class="text-background-500 block text-sm">Als beheerder en/of eigenaar</span>
            </span>
          </button>
          <button
            type="button"
            class="hover:bg-background-100 flex items-center gap-3 rounded-lg p-2 text-left transition-colors"
            @click="openShareDialog('installateur')"
          >
            <i class="pi pi-wrench text-background-500" />
            <span>
              <span class="block font-medium">Installateur</span>
              <span class="text-background-500 block text-sm">Leestoegang en herstelpunten</span>
            </span>
          </button>
        </div>
      </Popover>

      <ShareRapportDialog
        v-if="shareDialogVisible"
        v-model:visible="shareDialogVisible"
        :rapport-id="routeRapportId"
        :share-type="shareDialogType"
      />

      <DocumentVereistenSection
        class="mb-4"
        :rapport-id="routeRapportId"
        :active-inspectie-type="activeInspectieType"
      />

      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <!-- Locatie -->
        <section
          v-if="(rapport as any).locatie_snapshot"
          class="detail-section"
        >
          <h3 class="section-title">Locatie</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Naam</span>
              <span class="info-value">{{ display((rapport as any).locatie_snapshot.naam) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Adres</span>
              <span class="info-value">
                {{ (rapport as any).locatie_snapshot.straat || "" }}
                {{ (rapport as any).locatie_snapshot.huisnr || "" }}
              </span>
            </div>
            <div class="info-item">
              <span class="info-label">Plaats</span>
              <span class="info-value">{{ display((rapport as any).locatie_snapshot.plaats) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Bouwjaar</span>
              <span class="info-value">{{ display((rapport as any).locatie_snapshot.bouwJaar) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Type Object</span>
              <span class="info-value">{{ display((rapport as any).locatie_snapshot.typeObject) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Gebruiksdoel</span>
              <span class="info-value">{{ display((rapport as any).locatie_snapshot.gebruiksDoel) }}</span>
            </div>
          </div>
          <div class="photo-section">
            <h4 class="photo-section-title">Locatie foto's</h4>
            <div
              v-if="locatieFotoBoven"
              class="photo-hero"
            >
              <button
                type="button"
                class="photo-hero-button"
                @click="openPhotoPreview(locatieFotoBoven)"
              >
                <img
                  :ref="(el) => setThumbRef(locatieFotoBoven?.id || 'locatie-foto-boven', el as HTMLElement | null)"
                  :src="locatieFotoBoven.remote_url || ''"
                  :alt="getLocatieFotoLabel(locatieFotoBoven, 0)"
                  class="photo-hero-image"
                />
              </button>
              <p class="photo-caption">{{ getLocatieFotoLabel(locatieFotoBoven, 0) }}</p>
            </div>
            <div
              v-if="locatieOverzichtFotos.length > 0"
              class="photo-grid"
            >
              <button
                v-for="(photo, index) in locatieOverzichtFotos"
                :key="photo.id"
                type="button"
                class="photo-tile"
                @click="openPhotoPreview(photo)"
              >
                <img
                  :ref="(el) => setThumbRef(photo.id, el as HTMLElement | null)"
                  :src="photo.remote_url || ''"
                  :alt="getLocatieFotoLabel(photo, index)"
                  class="photo-image"
                />
                <span class="photo-caption">{{ getLocatieFotoLabel(photo, index) }}</span>
              </button>
            </div>
            <div
              v-if="pvOverzichtFotos.length > 0"
              class="photo-grid"
            >
              <button
                v-for="(photo, index) in pvOverzichtFotos"
                :key="photo.id"
                type="button"
                class="photo-tile"
                @click="openPhotoPreview(photo)"
              >
                <img
                  :ref="(el) => setThumbRef(photo.id, el as HTMLElement | null)"
                  :src="photo.remote_url || ''"
                  :alt="getPvOverzichtFotoLabel(photo, index)"
                  class="photo-image"
                />
                <span class="photo-caption">{{ getPvOverzichtFotoLabel(photo, index) }}</span>
              </button>
            </div>
            <p
              v-if="!locatieFotoBoven && locatieOverzichtFotos.length === 0 && pvOverzichtFotos.length === 0"
              class="empty-text"
            >
              Geen locatie foto's beschikbaar.
            </p>
          </div>
        </section>
        <!-- Installatie -->
        <section class="detail-section">
          <h3 class="section-title">Installatie</h3>
          <div class="info-grid">
            <div class="info-item">
              <span class="info-label">Net Spanning</span>
              <span class="info-value">{{ display(rapport.installatie?.netSpanning) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Stroom Stelsel</span>
              <span class="info-value">{{ display(rapport.installatie?.stroomStelsel) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Net Aansluiting</span>
              <span class="info-value">{{ display(rapport.installatie?.netAansluiting) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Installatie Jaar</span>
              <span class="info-value">{{ display(rapport.installatie?.installatieJaar) }}</span>
            </div>
            <div class="info-item">
              <span class="info-label">Oppervlakte</span>
              <span class="info-value">
                {{
                  (rapport as any).offerte_snapshot?.basis?.aM2
                    ? `${(rapport as any).offerte_snapshot.basis.aM2} m²`
                    : "Niet beschikbaar"
                }}
              </span>
            </div>
          </div>
          <div class="photo-section">
            <h4 class="photo-section-title">Installatie foto's</h4>
            <div
              v-if="installatieFotos.length > 0"
              class="photo-grid photo-grid--compact"
            >
              <button
                v-for="(photo, index) in installatieFotos"
                :key="photo.id"
                type="button"
                class="photo-tile"
                @click="openPhotoPreview(photo)"
              >
                <img
                  :ref="(el) => setThumbRef(photo.id, el as HTMLElement | null)"
                  :src="photo.remote_url || ''"
                  :alt="getInstallatieFotoLabel(photo, index)"
                  class="photo-image"
                />
                <span class="photo-caption">{{ getInstallatieFotoLabel(photo, index) }}</span>
              </button>
            </div>
            <p
              v-else
              class="empty-text"
            >
              Geen installatie foto's beschikbaar.
            </p>
          </div>
        </section>
      </div>

      <!-- Constateringen Summary -->
      <section class="detail-section constateringen-section">
        <h3 class="section-title">Constateringen {{ activeInspectieLabel }}</h3>
        <div
          v-if="constateringenSummary.total > 0"
          class="constateringen-summary"
        >
          <div class="summary-stat">
            <span class="stat-count">{{ constateringenSummary.total }}</span>
            <span class="stat-label">Totaal</span>
          </div>
          <div
            v-if="constateringenSummary.opmerking > 0"
            class="summary-stat"
          >
            <span class="stat-count stat-opmerking">{{ constateringenSummary.opmerking }}</span>
            <span class="stat-label">Opmerking</span>
          </div>
          <div
            v-if="constateringenSummary.gering > 0"
            class="summary-stat"
          >
            <span class="stat-count stat-gering">{{ constateringenSummary.gering }}</span>
            <span class="stat-label">Gering</span>
          </div>
          <div
            v-if="constateringenSummary.serieus > 0"
            class="summary-stat"
          >
            <span class="stat-count stat-serieus">{{ constateringenSummary.serieus }}</span>
            <span class="stat-label">Serieus</span>
          </div>
          <div
            v-if="constateringenSummary.ernstig > 0"
            class="summary-stat"
          >
            <span class="stat-count stat-ernstig">{{ constateringenSummary.ernstig }}</span>
            <span class="stat-label">Ernstig</span>
          </div>
          <div class="summary-stat">
            <span class="stat-count stat-hersteld">{{ constateringenSummary.hersteld }}</span>
            <span class="stat-label">Hersteld</span>
          </div>
        </div>
        <p
          v-else
          class="empty-text"
        >
          Geen constateringen gevonden.
        </p>

        <div v-if="hasHerinspectie" class="mt-6 space-y-3 rounded-sm border border-amber-200 bg-amber-50 p-4">
          <div>
            <p class="text-sm font-semibold text-amber-900">Herinspectie vereist</p>
            <p class="mt-1 text-sm text-amber-800">
              U kunt de herstelpunten en herstelfoto's gewoon aanleveren. De constateringen die hiervoor zijn gemarkeerd
              krijgen daarna nog een fysieke herinspectie door de inspecteur.
            </p>
          </div>

          <div class="space-y-3">
            <div
              v-for="item in herinspectieStatusItems"
              :key="item.id"
              class="rounded-sm border border-amber-200 bg-white p-3"
            >
              <div class="flex flex-wrap items-center gap-2">
                <span class="font-semibold text-amber-900">{{ item.locatie }}</span>
                <CustomTag :color="item.beoordeling" :label="item.beoordeling" />
                <span class="rounded-full px-2 py-1 text-xs font-medium" :class="item.bevestigd ? 'bg-green-100 text-green-700' : item.hersteld ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'">
                  {{ item.bevestigd ? 'Bevestigd' : item.hersteld ? 'Hersteld' : 'Openstaand' }}
                </span>
              </div>
              <p v-if="item.opmerkingen" class="mt-2 text-sm text-slate-700">{{ item.opmerkingen }}</p>
              <p v-if="item.lastMessage" class="mt-2 text-sm text-slate-500">Laatste bericht: {{ item.lastMessage }}</p>
            </div>
          </div>
        </div>

        <div v-if="showStandaloneHerstelButton" class="mt-4">
          <Button
            :label="isScopeSubmitted ? 'Bekijk herstelpunten' : 'Herstelpunten invullen'"
            severity="secondary"
            @click="router.push(rapport && activeInspectieType ? getRapportHerstelPath(rapport.id, activeInspectieType) : '/rapporten')"
          />
        </div>

        <!-- Process flow footer -->
        <div
          v-if="shouldShowProcessFooter && processCta"
          class="process-footer"
        >
          <p class="process-footer-label">Volgende stap</p>
          <div class="process-steps">
            <template
              v-for="(step, index) in processSteps"
              :key="step.key"
            >
              <div
                class="process-step"
                :class="`step-${step.state}`"
              >
                <div class="step-bubble">
                  <i
                    v-if="step.state === 'done'"
                    class="pi pi-check"
                  />
                  <template v-else>{{ index + 1 }}</template>
                </div>
                <span class="step-text">{{ step.label }}</span>
              </div>
              <div
                v-if="index < processSteps.length - 1"
                class="process-connector"
                :class="{ 'connector-active': processConnectorStates[index] }"
              />
            </template>
          </div>
          <HerstelInstructies
            v-if="processCta.label === 'Herstelpunten invullen' && hasHerstelbareConstateringen"
            class="process-instructies"
            variant="details"
            :scope-label="activeInspectieLabel"
            :has-herinspectie="hasHerinspectie"
          />
          <div class="process-cta">
            <p class="process-cta-description">{{ processCta.description }}</p>
            <p
              v-if="scopeSubmissionDate"
              class="process-cta-meta"
            >
              Herstelverklaring ingediend op {{ formatDate(scopeSubmissionDate) }}
            </p>
            <Button
              v-if="processCta.label && processCta.path"
              :label="processCta.label"
              icon="pi pi-arrow-right"
              iconPos="right"
              @click="router.push(processCta.path)"
              size="large"
            />
            <Button
              v-if="canDownloadCertificaat"
              label="Download certificaat"
              icon="pi pi-download"
              iconPos="right"
              :loading="certificaatGenerating"
              @click="handleDownloadCertificaat"
              size="large"
            />
          </div>
        </div>
      </section>

      <NotitiesSection
        :entity-type="'rapport'"
        :entity-id="routeRapportId"
      />
    </template>
  </div>
</template>

<style scoped>
  .rapport-details-page {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding-bottom: 2rem;
  }

  /* Room for the fixed PDF download bar (RapportPdfCard) on mobile. */
  @media (max-width: 767px) {
    .rapport-details-page {
      padding-bottom: calc(6rem + env(safe-area-inset-bottom));
    }
  }

  .status-card {
    border-radius: 0.5rem;
    border: 1px solid var(--p-background-200);
    background: var(--p-background-0);
    padding: 2rem;
    text-align: center;
    color: var(--p-background-600);
  }

  .status-loading {
    display: flex;
    justify-content: center;
  }

  .status-error {
    border-color: var(--p-red-200);
    background: var(--p-red-50);
    color: var(--p-red-600);
  }

  .rapport-card {
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 0.5rem;
    padding: 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .header-card-top {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: space-between;
    align-items: center;
  }

  .rapport-nr {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--p-background-900);
    margin: 0;
  }

  .rapport-date {
    font-size: 0.85rem;
    color: var(--p-background-500);
    margin: 0.25rem 0 0;
  }

  .detail-section {
    background: var(--p-background-0);
    border: 1px solid var(--p-background-200);
    border-radius: 0.5rem;
    padding: 1.25rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    min-width: 40%;
  }

  .section-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--p-background-800);
    margin: 0 0 1rem;
  }

  .inspectie-cards {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    width: max-content;
  }

  .inspectie-card {
    background: var(--p-background-50);
    border: 1px solid var(--p-background-200);
    border-radius: 0.375rem;
    padding: 0.75rem 1rem;
  }

  .inspectie-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 0.75rem;
  }

  .inspectie-type {
    font-weight: 600;
    color: var(--p-background-800);
  }

  .inspectie-date {
    font-size: 0.8rem;
    color: var(--p-background-500);
    margin: 0.5rem 0 0;
  }

  .pdf-document-row {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.5rem 0.75rem;
  }

  .pdf-document-icon {
    color: var(--color-voldoet);
    font-size: 1.1rem;
  }

  .pdf-document-name {
    flex: 1;
    color: var(--p-background-900);
    font-weight: 600;
  }

  .inspectie-certificaat {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    margin-top: 0.75rem;
  }

  .info-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: 1rem;
  }

  .info-item {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .info-label {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--p-background-500);
  }

  .info-value {
    font-size: 0.95rem;
    color: var(--p-background-800);
  }

  .photo-section {
    margin-top: 1.5rem;
  }

  .photo-section-title {
    margin: 0 0 0.75rem;
    font-size: 0.9rem;
    font-weight: 600;
    color: var(--p-background-700);
  }

  .photo-hero {
    margin-bottom: 1rem;
  }

  .photo-hero-button,
  .photo-tile {
    width: 100%;
    border: 1px solid var(--p-background-200);
    border-radius: 0.5rem;
    background: var(--p-background-50);
    padding: 0;
    overflow: hidden;
    cursor: pointer;
    text-align: left;
  }

  .photo-hero-button {
    display: block;
  }

  .photo-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 1rem;
  }

  .photo-grid--compact {
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  }

  .photo-hero-image,
  .photo-image {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    background: var(--p-background-100);
  }

  .photo-hero-image {
    max-height: 22rem;
    min-height: 14rem;
  }

  .photo-image {
    aspect-ratio: 4 / 3;
  }

  .photo-caption {
    display: block;
    padding: 0.65rem 0.85rem;
    font-size: 0.82rem;
    font-weight: 500;
    color: var(--p-background-700);
  }

  .constateringen-summary {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
  }

  .summary-stat {
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 60px;
  }

  .stat-count {
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--p-background-800);
  }

  .stat-label {
    font-size: 0.75rem;
    font-weight: 500;
    color: var(--p-background-500);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .stat-opmerking {
    color: var(--color-opmerking, #3b82f6);
  }
  .stat-gering {
    color: var(--color-gering, #f59e0b);
  }
  .stat-serieus {
    color: var(--color-serieus, #f97316);
  }
  .stat-ernstig {
    color: var(--color-ernstig, #ef4444);
  }
  .stat-hersteld {
    color: var(--color-hersteld, #22c55e);
  }

  .empty-text {
    color: var(--p-background-500);
    font-size: 0.9rem;
  }

  .constateringen-section {
    overflow: hidden;
  }

  .process-footer {
    margin-top: 1.5rem;
    border-top: 1px solid var(--p-background-200);
    background: linear-gradient(to top, var(--p-background-50), var(--p-background-0));
    padding: 1.25rem 1.25rem 1.5rem;
    margin-left: -1.25rem;
    margin-right: -1.25rem;
  }

  .process-footer-label {
    font-size: 0.7rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--p-primary-500, #3b82f6);
    margin: 0 0 1rem;
  }

  .process-steps {
    display: flex;
    align-items: flex-start;
    gap: 0;
    flex-wrap: nowrap;
    overflow-x: auto;
    padding: 0.25rem;
  }

  .process-step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.4rem;
    flex-shrink: 0;
    min-width: 80px;
    max-width: 100px;
  }

  .step-bubble {
    width: 2.25rem;
    height: 2.25rem;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    font-size: 0.9rem;
    flex-shrink: 0;
    border: 2px solid transparent;
  }

  .step-text {
    font-size: 0.72rem;
    font-weight: 500;
    text-align: center;
    line-height: 1.3;
  }

  .step-done .step-bubble {
    background: var(--p-green-500, #22c55e);
    color: #fff;
    border-color: var(--p-green-500, #22c55e);
  }

  .step-done .step-text {
    color: var(--p-green-700, #15803d);
  }

  .step-active .step-bubble {
    background: var(--p-primary-500, #3b82f6);
    color: #fff;
    border-color: var(--p-primary-500, #3b82f6);
    box-shadow: 0 0 0 4px var(--p-primary-100, #dbeafe);
  }

  .step-active .step-text {
    color: var(--p-primary-700, #1d4ed8);
    font-weight: 700;
  }

  .step-pending .step-bubble {
    background: var(--p-background-0);
    color: var(--p-background-400);
    border-color: var(--p-background-300);
  }

  .step-pending .step-text {
    color: var(--p-background-400);
  }

  .process-connector {
    flex: 1;
    height: 2px;
    background: var(--p-background-300);
    margin-top: calc(1.125rem - 1px);
    min-width: 1rem;
  }

  .connector-active {
    background: var(--p-primary-400, #60a5fa);
  }

  .process-instructies {
    margin-top: 1rem;
  }

  .process-cta {
    margin-top: 1.25rem;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }

  .process-cta-description {
    font-size: 0.875rem;
    color: var(--p-background-700);
    margin: 0;
    line-height: 1.5;
  }

  .process-cta-meta {
    font-size: 0.85rem;
    color: var(--p-background-500);
    margin: 0;
  }

  .herstel-cta {
    text-align: center;
  }

  @media (max-width: 640px) {
    .info-grid {
      grid-template-columns: 1fr 1fr;
    }

    .constateringen-summary {
      gap: 1rem;
    }

    .photo-grid,
    .photo-grid--compact {
      grid-template-columns: 1fr 1fr;
    }
  }
</style>
